import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  FileText,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Sparkles,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react';
import { api } from '../../api/client';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { SkeletonGrid } from '../../components/Skeleton';
import { PLATFORM_COLORS, PLATFORM_NAMES } from '../../constants/platforms';
import { formatNumber } from '../../lib/format';
import CsvExportButton from '../../components/CsvExportButton';

export default function AITrafficTab({ domain }) {
  const { data, isLoading } = useQuery({
    queryKey: ['traffic', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/analytics/referrals?days=30`)
        .then((r) => r.data),
  });

  if (isLoading)
    return (
      <div className="space-y-6">
        <PageHeader
          variant="gradient"
          icon={LineChartIcon}
          eyebrow="Analytics"
          title="AI traffic"
          description="See which AI engines are sending visitors to your site, which pages they land on, and how that mix is changing over time."
        />
        <SkeletonGrid count={4} />
      </div>
    );
  if (!data) return null;

  if (data.totalVisitors === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          variant="gradient"
          icon={LineChartIcon}
          eyebrow="Analytics"
          title="AI traffic"
          description="See which AI engines are sending visitors to your site, which pages they land on, and how that mix is changing over time."
          help={{
            title: 'What counts as AI traffic?',
            body:
              'Any visit whose referrer matches ChatGPT, Claude, Perplexity, Gemini, Copilot, or another known AI engine. The tracking script tags these in real time.',
          }}
        />
        <EmptyState
          icon={Sparkles}
          title="No AI referrals yet"
          body="Once visitors arrive from ChatGPT, Claude, Perplexity, or other AI engines, you'll see them broken down here. Make sure the tracking script is installed in Settings."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        variant="gradient"
        icon={LineChartIcon}
        eyebrow="Analytics"
        title="AI traffic"
        description="Last 30 days of visits from AI engines, broken down by source and landing page."
        help={{
          title: 'What counts as AI traffic?',
          body:
            'Any visit whose referrer matches ChatGPT, Claude, Perplexity, Gemini, Copilot, or another known AI engine. The tracking script tags these in real time.',
        }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total AI visitors · 30d"
          value={formatNumber(data.totalVisitors)}
          icon={Users}
          variant="brand"
        />
        <StatCard
          label="Top source"
          value={data.bySource[0]?.name || '—'}
          hint={`${formatNumber(data.bySource[0]?.count || 0)} visits`}
          icon={Trophy}
          variant="amber"
        />
        <StatCard
          label="Distinct platforms"
          value={data.bySource.length}
          icon={TrendingUp}
          variant="cyan"
        />
        <StatCard
          label="Top pages tracked"
          value={data.topPages.length}
          icon={FileText}
          variant="green"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <PieChartIcon className="w-4 h-4" />
              </span>
              AI referral sources
            </div>
            <CsvExportButton domainId={domain._id} kind="ai-traffic" label="CSV" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={data.bySource}
                dataKey="count"
                nameKey="name"
                outerRadius={100}
                label
              >
                {data.bySource.map((entry) => (
                  <Cell key={entry.source} fill={PLATFORM_COLORS[entry.source] || '#888'} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <LineChartIcon className="w-4 h-4" />
              </span>
              Traffic over time
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#716b8b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#716b8b' }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Legend />
              {Object.keys(PLATFORM_COLORS).map((src) => (
                <Line
                  key={src}
                  type="monotone"
                  dataKey={src}
                  stroke={PLATFORM_COLORS[src]}
                  name={PLATFORM_NAMES[src]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <FileText className="w-4 h-4" />
            </span>
            Top pages by AI traffic
          </div>
        </div>
        {data.topPages.length === 0 ? (
          <p className="text-sm text-gray-600">No per-page data yet.</p>
        ) : (
          <div className="overflow-x-auto scroll-soft">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Page</th>
                  {Object.keys(PLATFORM_NAMES).map((s) => (
                    <th key={s} className="text-right">
                      {PLATFORM_NAMES[s]}
                    </th>
                  ))}
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.topPages.map((row) => (
                  <tr key={row.page}>
                    <td className="truncate max-w-[20rem] text-ink-800">{row.page}</td>
                    {Object.keys(PLATFORM_NAMES).map((s) => (
                      <td key={s} className="text-right text-ink-400">
                        {row.sources[s] || ''}
                      </td>
                    ))}
                    <td className="text-right font-semibold text-ink-900">
                      {formatNumber(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
